import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { eventService } from '../api/eventService';
import { Event } from '../types/event.types';
import { AttendeeType } from '../types/enums';
import toast from 'react-hot-toast';
import { CACHE_TIME, QUERY_CONFIG, TOAST_DURATION } from '../types/constants';

interface EventSearchFilters {
  search?: string;
  eventType?: string;
  status?: string;
  startDate?: string;
  hasAvailableSpots?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
}

interface EventSearchResponse {
  events: Event[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export const useEvents = (filters?: EventSearchFilters) => {
  return useQuery<EventSearchResponse, Error>({
    queryKey: ['member-events', filters],
    queryFn: () => eventService.getEvents(filters),
    staleTime: CACHE_TIME.DEFAULT,
    refetchOnWindowFocus: QUERY_CONFIG.REFETCH_ON_FOCUS,
    retry: QUERY_CONFIG.RETRY_COUNT,
  });
};

export const useUpcomingEvents = () => {
  // Get start of today in UTC
  const now = new Date();
  const todayUtcStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())).toISOString();

  const { data, ...rest } = useEvents({
    sortBy: 'startDateTime:ASC',
    startDate: todayUtcStart,
    // TODO: fix it to 20 with self updating
    limit: 1000, // Fetch up to 1000 events to show all upcoming events
  });

  return {
    data: data?.events || [],
    ...rest,
  };
};

export const useCheckIn = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  return useMutation({
    mutationFn: async ({
      eventId,
      attendees,
      checkedInByName,
      emergencyContactName,
      emergencyContactPhone,
    }: {
      eventId: string;
      attendees: Array<{ id: string; name?: string; type?: AttendeeType }>;
      checkedInByName?: string;
      emergencyContactName?: string;
      emergencyContactPhone?: string;
    }) => {
      const results = await Promise.allSettled(
        attendees.map((attendee) => {
          return eventService.checkInAttendee(
            eventId,
            attendee.id,
            attendee.name,
            checkedInByName,
            attendee.type,
            emergencyContactName,
            emergencyContactPhone,
          );
        }),
      );

      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - succeeded;

      const errorDetails: Array<{ attendeeName: string; message: string; errorType?: string }> = [];
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const attendeeName = attendees[index].name || attendees[index].id;
          let errorMessage = '';
          let errorType = 'unknown';

          if (result.reason?.response?.data?.error?.message) {
            errorMessage = result.reason.response.data.error.message;
          } else if (result.reason?.response?.data?.message) {
            errorMessage = result.reason.response.data.message;
          } else if (result.reason?.message) {
            errorMessage = result.reason.message;
          }

          // Determine error type for translation
          if (errorMessage.includes('requires pre-registration')) {
            errorType = 'preRegistrationRequired';
          } else if (errorMessage.includes('already checked in')) {
            errorType = 'alreadyCheckedIn';
          } else if (errorMessage.includes('does not allow children')) {
            errorType = 'adultsOnly';
          } else if (errorMessage.includes('does not allow adults')) {
            errorType = 'childrenOnly';
          }

          errorDetails.push({
            attendeeName,
            message: errorMessage,
            errorType,
          });
        }
      });

      return { succeeded, failed, total: results.length, errors: errorDetails };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['member-events'] });

      if (data.failed > 0) {
        if (data.errors && data.errors.length > 0) {
          data.errors.forEach((error) => {
            const translatedMessage =
              error.errorType && error.errorType !== 'unknown' ? t(`events.errors.${error.errorType}`) : error.message;

            toast.error(`${error.attendeeName}: ${translatedMessage}`, {
              duration: error.errorType === 'alreadyCheckedIn' ? TOAST_DURATION.DEFAULT : TOAST_DURATION.LONG,
            });
          });
        }

        if (data.succeeded > 0) {
          toast.success(t('events.success.checkInPartial', { succeeded: data.succeeded, total: data.total }), {
            duration: TOAST_DURATION.DEFAULT,
          });
        }
      } else {
        toast.success(t('events.success.checkInAll', { count: data.succeeded }), {
          duration: TOAST_DURATION.SHORT,
        });
      }
    },
    onError: () => {
      toast.error(t('events.errors.checkInFailed'), {
        duration: TOAST_DURATION.DEFAULT,
      });
    },
  });
};

export const useCheckOut = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  return useMutation({
    mutationFn: async ({ eventId, attendeeId }: { eventId: string; attendeeId: string }) => {
      return eventService.checkOutAttendee(eventId, attendeeId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['member-events'] });

      toast.success(t('events.success.checkOutSuccess'));
    },
    onError: (error: unknown) => {
      let errorMessage = '';

      const err = error as {
        response?: { data?: { error?: { message?: string }; message?: string } };
        message?: string;
      };
      if (err?.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      if (errorMessage.includes('requires pre-registration')) {
        toast.error(t('events.errors.cannotCheckOutPreReg'), {
          duration: TOAST_DURATION.LONG,
        });
      } else if (errorMessage.includes('already checked out')) {
        toast.error(t('events.errors.alreadyCheckedOut'), {
          duration: TOAST_DURATION.DEFAULT,
        });
      } else if (errorMessage.includes('not checked in')) {
        toast.error(t('events.errors.notCheckedIn'), {
          duration: TOAST_DURATION.DEFAULT,
        });
      } else {
        toast.error(errorMessage || t('events.errors.checkOutFailed'), {
          duration: TOAST_DURATION.LONG,
        });
      }
    },
  });
};
